import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { adminApi } from './api';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '../components/ui/sheet';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { Separator } from '../components/ui/separator';
import {
  Mail,
  MessageCircle,
  NotebookPen,
  Sparkles,
  Flame,
  Target,
  CreditCard,
  Send,
  Download,
  Brain,
  Activity,
  ChevronDown,
  ChevronRight,
  Loader2,
  ExternalLink,
  TrendingUp,
  Calendar,
} from 'lucide-react';
import { logger } from '../lib/logger';
import { toast } from 'sonner';

/**
 * SubscriberDetailDrawer (Phase 29)
 *
 * 360° drilldown panel opened from each row on /app/admin/subscribers.
 * Fetches /api/admin/subscribers/{email} once on open and lazily fetches
 * full chat transcripts when an admin clicks a session.
 *
 * Zero LLM credit usage — backend is pure Mongo aggregations + reuse of the
 * Phase 28 progress helpers.
 */

const fmtRelative = (iso) => {
  if (!iso) return '—';
  try {
    const diff = Date.now() - new Date(iso).getTime();
    if (diff < 0) return 'Just now';
    const m = Math.floor(diff / 60000);
    if (m < 1) return 'Just now';
    if (m < 60) return `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(h / 24);
    if (d < 30) return `${d}d ago`;
    return new Date(iso).toLocaleDateString();
  } catch { return ''; }
};

const fmtDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch { return ''; }
};

const fmtMoney = (cents, currency = 'usd') => {
  if (cents == null) return '—';
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: (currency || 'usd').toUpperCase(),
    }).format(cents / 100);
  } catch { return `$${(cents / 100).toFixed(2)}`; }
};

const TierBadge = ({ tier, trialUntil }) => {
  const t = (tier || 'free').toLowerCase();
  const styles =
    t === 'pro' ? 'bg-[#004C54]/10 text-[#004C54]' :
    t === 'premium' ? 'bg-[#0B0C0D] text-white' :
    'bg-secondary text-foreground/70';
  let trialDays = null;
  if (trialUntil) {
    try {
      // Date.now() in render body is intentional for the live countdown badge;
      // suppress react-hooks/purity for this read-only time math.
      
      const ms = new Date(trialUntil).getTime() - Date.now();
      if (ms > 0) trialDays = Math.max(1, Math.ceil(ms / 86400000));
    } catch { /* noop */ }
  }
  return (
    <span className="inline-flex items-center">
      <span className={`inline-flex items-center px-2 h-5 rounded text-[10px] font-mono-accent font-bold uppercase tracking-[0.16em] ${styles}`}>
        {t.toUpperCase()}
      </span>
      {trialDays != null && (
        <span className="ml-1.5 inline-flex items-center gap-1 px-1.5 h-4 rounded text-[9px] font-mono-accent font-bold uppercase tracking-[0.14em] bg-[#4FBCC7]/15 text-[#0a6066]">
          Trial · {trialDays}d
        </span>
      )}
    </span>
  );
};

const SectionHeader = ({ icon: Icon, label, count, testid }) => (
  <div className="flex items-center gap-2 text-[10.5px] font-mono-accent uppercase tracking-[0.2em] text-foreground/55 font-semibold mb-2" data-testid={testid}>
    <Icon className="h-3 w-3" />
    {label}
    {count != null && (
      <span className="text-foreground/40">· {count}</span>
    )}
  </div>
);

const WeeklyBars = ({ bars }) => {
  if (!Array.isArray(bars) || bars.length === 0) {
    return <div className="text-[12px] text-foreground/50">No 7-day activity yet.</div>;
  }
  const max = Math.max(1, ...bars.map((b) => Number(b.total || 0)));
  return (
    <div className="flex items-end gap-1.5 h-14">
      {bars.map((b, i) => {
        const total = Number(b.total || 0);
        const h = Math.max(6, Math.round((total / max) * 56));
        return (
          <div key={b.date || b.label || `bar-${i}`} className="flex-1 flex flex-col items-center gap-1">
            <div
              className={`w-full rounded-t ${total > 0 ? 'bg-[#004C54]' : 'bg-secondary/70'}`}
              style={{ height: `${h}px` }}
              title={`${b.label || b.date}: ${total}`}
            />
            <div className="text-[9.5px] font-mono-accent uppercase tracking-[0.14em] text-foreground/55">
              {(b.label || '').slice(0, 3)}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Single chat session row: shows preview + msg count; expands to full transcript.
const ChatSessionRow = ({ session, email }) => {
  const [open, setOpen] = useState(false);
  const [transcript, setTranscript] = useState(null);
  const [loading, setLoading] = useState(false);

  const toggle = useCallback(async () => {
    const next = !open;
    setOpen(next);
    if (next && !transcript) {
      setLoading(true);
      try {
        const data = await adminApi.subscriberChatTranscript(email, session.session_id);
        setTranscript(data?.messages || []);
      } catch (err) {
        logger.warn('chat transcript load failed', err);
        toast.error(err?.message || 'Could not load transcript');
        setOpen(false);
      } finally {
        setLoading(false);
      }
    }
  }, [open, transcript, email, session.session_id]);

  return (
    <li className="rounded-lg border border-border/60 overflow-hidden" data-testid={`admin-sub-chat-${session.session_id}`}>
      <button
        type="button"
        onClick={toggle}
        className="w-full text-left p-3 hover:bg-secondary/30 transition-colors"
        aria-expanded={open}
      >
        <div className="flex items-center justify-between text-[11px] text-foreground/55 mb-1">
          <span className="font-mono-accent uppercase tracking-[0.14em] font-semibold">
            {session.msg_count} message{session.msg_count === 1 ? '' : 's'}
          </span>
          <span className="inline-flex items-center gap-1.5">
            {fmtRelative(session.last_at)}
            {open
              ? <ChevronDown className="h-3 w-3" />
              : <ChevronRight className="h-3 w-3" />}
          </span>
        </div>
        {session.preview && (
          <div className="text-[12.5px] text-foreground/80 line-clamp-2">
            “{session.preview}”
          </div>
        )}
      </button>
      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-border/40 bg-background/40">
          {loading ? (
            <div className="py-3 text-[12px] text-foreground/55 inline-flex items-center gap-2">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading transcript…
            </div>
          ) : !transcript || transcript.length === 0 ? (
            <div className="py-3 text-[12px] text-foreground/55 italic">No messages.</div>
          ) : (
            <ol className="space-y-2 mt-3" data-testid={`admin-sub-chat-transcript-${session.session_id}`}>
              {transcript.map((m, i) => {
                const isUser = (m.role || '').toLowerCase() === 'user';
                return (
                  <li
                    key={m.id || m.message_id || `${m.created_at}-${i}`}
                    className={`text-[12.5px] leading-relaxed whitespace-pre-wrap px-3 py-2 rounded-lg ${
                      isUser
                        ? 'bg-[#004C54]/8 text-foreground'
                        : 'bg-secondary/50 text-foreground/85'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-1 text-[10px] uppercase tracking-[0.16em] font-mono-accent font-bold opacity-60">
                      <span>{isUser ? 'User' : 'Coach AI'}</span>
                      <span>{fmtRelative(m.created_at)}</span>
                    </div>
                    {m.content}
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </li>
  );
};

const JournalRow = ({ entry }) => {
  const [open, setOpen] = useState(false);
  const text = entry.content || entry.entry || entry.reflection || entry.text || '';
  const long = text.length > 240;
  return (
    <li className="rounded-lg border border-border/60 p-3" data-testid={`admin-sub-journal-${entry.id || entry.entry_id || entry.created_at}`}>
      <div className="flex items-center justify-between text-[11px] text-foreground/55 mb-1">
        <span>{fmtRelative(entry.created_at)}</span>
        {entry.mood && (
          <Badge variant="outline" className="text-[10px]">{entry.mood}</Badge>
        )}
      </div>
      {text && (
        <div className={`text-[12.5px] text-foreground/85 whitespace-pre-wrap leading-relaxed ${open || !long ? '' : 'line-clamp-3'}`}>
          {text}
        </div>
      )}
      {long && (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="text-[11px] text-[#004C54] mt-1 font-bold uppercase tracking-[0.14em]"
        >
          {open ? 'Show less' : 'Show more'}
        </button>
      )}
      {Array.isArray(entry.themes) && entry.themes.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {entry.themes.slice(0, 6).map((t, i) => (
            <Badge key={typeof t === 'string' ? t : `theme-${i}`} variant="outline" className="text-[10px]">#{t}</Badge>
          ))}
        </div>
      )}
    </li>
  );
};

export default function SubscriberDetailDrawer({ open, email, onOpenChange }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Drawer-open fetch: setState calls are the legitimate outputs (load →
  // data / error). Suppress set-state-in-effect for the entire effect body.
  
  useEffect(() => {
    if (!open || !email) return;
    let cancelled = false;
    setData(null);
    setError(null);
    setLoading(true);
    (async () => {
      try {
        const d = await adminApi.subscriberDetail(email);
        if (!cancelled) setData(d);
      } catch (err) {
        logger.warn('subscriber detail load failed', err);
        if (!cancelled) setError(err?.message || 'Could not load profile');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, email]);

  const headline = useMemo(() => {
    if (!data) return { name: '', email: email || '' };
    return {
      name: data.identity?.first_name || data.app_user?.name || email,
      email: data.identity?.email || email,
    };
  }, [data, email]);

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-[640px] overflow-y-auto" data-testid="admin-sub-detail">
        <SheetHeader>
          <SheetTitle className="font-heading">{headline.name || headline.email}</SheetTitle>
          <SheetDescription className="text-[12.5px]">{headline.email}</SheetDescription>
        </SheetHeader>

        {loading ? (
          <div className="mt-6 space-y-3">
            <Skeleton className="h-20 w-full rounded-lg" />
            <Skeleton className="h-24 w-full rounded-lg" />
            <Skeleton className="h-32 w-full rounded-lg" />
          </div>
        ) : error ? (
          <div className="mt-6 text-[13px] text-red-700 bg-red-50 border border-red-200 rounded-lg p-3">
            {error}
          </div>
        ) : !data ? null : (
          <div className="mt-5 space-y-6" data-testid="admin-sub-detail-body">
            {/* Identity strip */}
            <div className="flex flex-wrap items-center gap-2">
              {data.app_user ? (
                <TierBadge tier={data.app_user.tier} trialUntil={data.app_user.trial_grant_until} />
              ) : (
                <Badge variant="outline" className="text-[10.5px]">Lead only</Badge>
              )}
              {(data.identity?.sources || []).map((s) => (
                <Badge key={s} variant="outline" className="text-[10.5px] capitalize">{s}</Badge>
              ))}
              {data.identity?.audience && (
                <Badge variant="outline" className="text-[10.5px] capitalize">
                  {data.identity.audience}
                </Badge>
              )}
              {data.app_user?.subscription_status && (
                <Badge variant="outline" className="text-[10.5px]">
                  sub: {data.app_user.subscription_status}
                </Badge>
              )}
              {data.app_user?.is_admin && (
                <Badge className="bg-[#0a0d0e] text-white text-[10.5px]">Admin</Badge>
              )}
              {data.identity?.newsletter?.unsubscribed_at && (
                <Badge className="bg-red-100 text-red-800 text-[10.5px] border border-red-200">
                  Unsubscribed
                </Badge>
              )}
            </div>

            {/* App activity (only if linked) */}
            {data.app_user && (
              <section>
                <SectionHeader icon={Activity} label="App activity" testid="admin-sub-section-activity" />
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { icon: MessageCircle, label: 'Chats',    value: data.app_user.chats || 0 },
                    { icon: Sparkles,      label: 'Pre-game', value: data.app_user.pregames || 0 },
                    { icon: NotebookPen,   label: 'Journals', value: data.app_user.journals || 0 },
                    { icon: Flame,         label: 'Streak',   value: `${data.app_user.streak_days || 0}d` },
                  ].map((s) => (
                    <div key={s.label} className="rounded-lg border border-border/60 p-2.5">
                      <div className="flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] text-foreground/55 font-mono-accent font-semibold">
                        <s.icon className="h-3 w-3" />
                        {s.label}
                      </div>
                      <div className="text-lg font-heading font-bold mt-1">{s.value}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-3">
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[10.5px] uppercase tracking-[0.18em] text-foreground/55 font-mono-accent font-semibold inline-flex items-center gap-1.5">
                      <TrendingUp className="h-3 w-3" /> Last 7 days
                    </div>
                    <div className="text-[11px] text-foreground/55">
                      Last active {fmtRelative(data.app_user.last_active)}
                    </div>
                  </div>
                  <WeeklyBars bars={data.weekly_activity} />
                </div>
                {Array.isArray(data.themes) && data.themes.length > 0 && (
                  <div className="mt-3">
                    <div className="text-[10.5px] uppercase tracking-[0.18em] text-foreground/55 font-mono-accent font-semibold mb-1.5">
                      Recurring themes
                    </div>
                    <div className="flex flex-wrap gap-1.5">
                      {data.themes.map((t, i) => {
                        const themeKey = t.label || t.keyword || t.term || t.word || `theme-${i}`;
                        return (
                          <Badge key={themeKey} variant="outline" className="text-[10.5px]">
                            #{t.label || t.keyword || t.term || t.word || '—'}
                            {t.count ? <span className="ml-1 text-foreground/45">· {t.count}</span> : null}
                          </Badge>
                        );
                      })}
                    </div>
                  </div>
                )}
              </section>
            )}

            {/* Nurture state */}
            {(data.nurture || []).length > 0 && (
              <section>
                <SectionHeader icon={Send} label="Nurture sequences" count={data.nurture.length} testid="admin-sub-section-nurture" />
                <ul className="space-y-2">
                  {data.nurture.map((n, i) => (
                    <li key={n.track || `nurture-${i}`} className="rounded-lg border border-border/60 p-3 text-[12.5px]" data-testid={`admin-sub-nurture-${n.track || i}`}>
                      <div className="flex items-center justify-between gap-2 flex-wrap">
                        <div className="font-semibold">{n.track || 'legacy 7-touch'}</div>
                        <div className="flex items-center gap-1.5">
                          {n.completed_sequence ? (
                            <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-800 border-emerald-200">Completed</Badge>
                          ) : n.unsubscribed_at ? (
                            <Badge variant="outline" className="text-[10px] bg-red-50 text-red-800 border-red-200">Unsubscribed</Badge>
                          ) : (
                            <Badge variant="outline" className="text-[10px]">Step {n.current_step}</Badge>
                          )}
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 mt-1.5 text-[11.5px] text-foreground/65">
                        <div>Started: {fmtDate(n.subscribed_at) || '—'}</div>
                        <div>Next email: {fmtDate(n.next_send_at) || '—'}</div>
                        {n.quiz_result && (
                          <div className="col-span-2">Quiz: <span className="font-medium">{n.quiz_result}</span></div>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Payment ledger */}
            {(data.payments?.subscriptions?.length > 0 ||
              data.payments?.one_time_purchases?.length > 0 ||
              data.payments?.stripe_customer_id) && (
              <section>
                <SectionHeader
                  icon={CreditCard}
                  label="Payments"
                  count={(data.payments.subscriptions?.length || 0) + (data.payments.one_time_purchases?.length || 0)}
                  testid="admin-sub-section-payments"
                />
                <div className="rounded-lg border border-border/60 divide-y divide-border/60 text-[12.5px]">
                  {data.payments.stripe_customer_id && (
                    <div className="p-3 flex items-center justify-between">
                      <span className="text-foreground/55">Stripe customer</span>
                      <a
                        href={`https://dashboard.stripe.com/customers/${data.payments.stripe_customer_id}`}
                        target="_blank" rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-[#004C54] font-mono-accent text-[11.5px]"
                        data-testid="admin-sub-stripe-link"
                      >
                        {data.payments.stripe_customer_id}
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  )}
                  {(data.payments.subscriptions || []).map((s, i) => (
                    <div
                      key={s.stripe_subscription_id || `${s.product}-${i}`}
                      className="p-3 flex items-center justify-between"
                    >
                      <div>
                        <div className="font-semibold">{s.product}</div>
                        <div className="text-[11px] text-foreground/55">
                          {[s.tier, s.interval].filter(Boolean).join(' · ')}
                        </div>
                      </div>
                      <Badge variant="outline" className="text-[10.5px]">{s.status || 'active'}</Badge>
                    </div>
                  ))}
                  {(data.payments.one_time_purchases || []).map((p, i) => (
                    <div
                      key={p.stripe_session_id || p.result_token || `${p.product}-${p.created_at || i}`}
                      className="p-3 flex items-center justify-between"
                    >
                      <div>
                        <div className="font-semibold">{p.product}</div>
                        <div className="text-[11px] text-foreground/55">{fmtDate(p.created_at)}</div>
                      </div>
                      <div className="font-mono-accent font-bold">
                        {fmtMoney(p.amount_cents, p.currency)}
                      </div>
                    </div>
                  ))}
                  {(data.payments.lifetime_amount_cents || 0) > 0 && (
                    <div className="p-3 flex items-center justify-between bg-secondary/30">
                      <span className="text-[11px] uppercase tracking-[0.18em] font-mono-accent font-semibold text-foreground/55">
                        Lifetime
                      </span>
                      <span className="font-mono-accent font-bold">
                        {fmtMoney(data.payments.lifetime_amount_cents)}
                      </span>
                    </div>
                  )}
                </div>
              </section>
            )}

            {/* Playbook downloads */}
            {(data.playbook_downloads || []).length > 0 && (
              <section>
                <SectionHeader icon={Download} label="Lead-magnet downloads" count={data.playbook_downloads.length} testid="admin-sub-section-downloads" />
                <ul className="space-y-1.5 text-[12.5px]">
                  {data.playbook_downloads.map((d, i) => (
                    <li
                      key={`${d.kind || 'playbook'}-${d.created_at || i}`}
                      className="flex items-center justify-between rounded-lg border border-border/60 p-2.5"
                    >
                      <span className="capitalize">{(d.kind || 'playbook').replaceAll('_', ' ')}</span>
                      <span className="text-foreground/55">{fmtRelative(d.created_at)}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Quiz attempts */}
            {(data.quiz_attempts || []).length > 0 && (
              <section>
                <SectionHeader icon={Brain} label="Quiz attempts" count={data.quiz_attempts.length} testid="admin-sub-section-quiz" />
                <ul className="space-y-2">
                  {data.quiz_attempts.map((q, i) => (
                    <li key={q.id || i} className="rounded-lg border border-border/60 p-3 text-[12.5px]">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="font-semibold">{q.archetype_label || q.archetype || '—'}</div>
                        <div className="text-[11px] text-foreground/55">{fmtDate(q.created_at)}</div>
                      </div>
                      <div className="text-[11px] text-foreground/55 capitalize mt-0.5">
                        {[q.audience, q.sport].filter(Boolean).join(' · ')}
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Goals */}
            {(data.goals || []).length > 0 && (
              <section>
                <SectionHeader icon={Target} label="Goals" count={data.goals.length} testid="admin-sub-section-goals" />
                <ul className="space-y-1.5">
                  {data.goals.map((g, i) => (
                    <li key={g.goal_id || i} className="text-[13px] flex items-start gap-2">
                      <span className={`mt-1 inline-block h-2 w-2 rounded-full ${g.completed_at ? 'bg-[#004C54]' : 'bg-foreground/30'}`} />
                      <div className="flex-1">
                        <div className={g.completed_at ? 'line-through text-foreground/55' : ''}>
                          {g.title || g.text || g.goal || '—'}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Chats */}
            {data.app_user && (
              <section>
                <SectionHeader icon={MessageCircle} label="Coach sessions" count={data.chat_sessions?.length || 0} testid="admin-sub-section-chats" />
                {data.chat_sessions?.length ? (
                  <ul className="space-y-2">
                    {data.chat_sessions.map((s) => (
                      <ChatSessionRow key={s.session_id} session={s} email={email} />
                    ))}
                  </ul>
                ) : (
                  <div className="text-[12px] text-foreground/55 italic">No chat sessions yet.</div>
                )}
              </section>
            )}

            {/* Journals */}
            {data.app_user && (
              <section>
                <SectionHeader icon={NotebookPen} label="Journal entries" count={data.journals?.length || 0} testid="admin-sub-section-journals" />
                {data.journals?.length ? (
                  <ul className="space-y-2">
                    {data.journals.map((j, i) => (
                      <JournalRow key={j.id || j.entry_id || i} entry={j} />
                    ))}
                  </ul>
                ) : (
                  <div className="text-[12px] text-foreground/55 italic">No journal entries yet.</div>
                )}
              </section>
            )}

            {/* Pregames */}
            {data.app_user && (data.pregames || []).length > 0 && (
              <section>
                <SectionHeader icon={Calendar} label="Pre-game plans" count={data.pregames.length} testid="admin-sub-section-pregames" />
                <ul className="space-y-2">
                  {data.pregames.map((p, i) => (
                    <li key={p.id || p.plan_id || i} className="rounded-lg border border-border/60 p-3 text-[12.5px]">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="font-semibold">{p.sport || p.opponent || 'Pre-game plan'}</div>
                        <div className="text-[11px] text-foreground/55">{fmtRelative(p.created_at)}</div>
                      </div>
                      {p.event_type && (
                        <div className="text-[11px] text-foreground/55 capitalize mt-0.5">{p.event_type}</div>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Lead-only state */}
            {!data.app_user && (
              <section className="rounded-lg border border-dashed border-border p-4 text-[12.5px] text-foreground/65 leading-relaxed" data-testid="admin-sub-lead-only">
                No app account is linked to this email yet. They&rsquo;ve engaged via{' '}
                <span className="font-semibold">{(data.identity?.sources || []).join(', ') || 'lead funnels'}</span>{' '}
                but haven&rsquo;t signed up for the Apex AI Coach app. Once they create an account using this email, their chats, journals, and progress will appear here.
              </section>
            )}

            <Separator />
            <div className="flex justify-end pt-1">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="admin-sub-detail-close">
                Close
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
